<template>
  <select class="selectize selectize-single" :name="name" :id="name" :placeholder="placeholder" :disabled="disabled" multiple></select>
</template>

<script>
import { isEqual, find, isNaN, toNumber, cloneDeep, filter, map } from 'lodash'
import 'selectize'
import $ from 'jquery'
import '../../../components/misc/infinite-scroll'

export default {
  props: {
    name: {
      required: true,
      type: String
    },
    keyContext: {
      required: false,
      default: ''
    },
    fetchMoreResults: {
      required: false,
      type: Function
    },
    options: {
      required: false,
      type: Array,
      default: function () {
        return []
      }
    },
    value: {
      required: false,
      default: null
    },
    placeholder: {
      required: false,
      default: ' '
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false
    },
    fetchResults: {
      required: false,
      type: Function
    },
    preLoad: {
      required: false,
      type: Boolean,
      default: true
    },
    itemAdded: {
      required: false,
      type: Function
    },
    itemRemoved: {
      required: false,
      type: Function
    },
    focus: {
      required: false,
      type: Function
    },
    blur: {
      required: false,
      type: Function
    },
    create: {
      required: false,
      default: false
    },
    render: {
      type: Object,
      required: false,
      default () {
        return {}
      }
    }
  },

  data () {
    return {
      element: null,
      selectize: null,
      selectedTags: [],
      first: true
    }
  },

  mounted () {
    this.element = $(this.$el)

    this.bind()
  },

  watch: {
    /**
     * Watch changes to the selected tags so we can emit it on the built-in "input" event
     * which should push changes to any v-models.
     *
     * @return void
     */
    selectedTags () {
      this.$emit('input', this.selectedTags)
    },

    /**
     * Watch the disabled prop for changes and refresh the picker.
     *
     * @return void
     */
    disabled (disabled) {
      this.toggleDisabled(disabled)
    },

    /**
     * Watch the options for changes and refresh the picker.
     *
     * @return void
     */
    options: {
      handler (newOptions, oldOptions) {
        if (isEqual(newOptions, oldOptions)) {
          return
        }

        let value = this.value

        this.selectize.clearOptions()

        this.selectize.addOption(cloneDeep(newOptions))

        if (!this.selectize.isOpen) {
          this.selectize.setValue(value, false)
        }
      },
      deep: true
    },

    /**
     * Watch the parent v-model for any changes any have them reflected on the
     * select picker. Allows programatic selection.
     *
     * @param mixed value
     *
     * @return void
     */
    value (value) {
      this.selectize.$dropdown.find('.selected').not('.active').removeClass('selected')

      let ids = map(value, (obj) => { return obj.id })
      this.selectize.setValue(ids, true)
    }
  },

  methods: {
    /**
     * Bind the selectize element.
     *
     * @return void
     */
    bind () {
      let plugins = ['remove_button']
      if (this.fetchMoreResults) {
        plugins.push('infinite_scroll')
      }

      this.selectize = this.element.selectize({
        plugins: plugins,
        options: cloneDeep(this.options),
        labelField: 'text',
        valueField: 'id',
        create: this.create,
        onItemAdd: (value) => {
          let tag = find(this.selectize.options, (tag) => String(tag.id) === String(value))

          if (tag) {
            tag = cloneDeep(tag)

            if (!this.tagAlreadySelected(tag)) {
              this.addSelectedTag(tag)

              this.itemAdded(tag, this.keyContext)
            }
          }
        },
        onItemRemove: (value) => {
          if (this.itemRemoved) {
            let tag = find(this.selectize.options, (tag) => String(tag.id) === String(value))

            if (tag) {
              tag = cloneDeep(tag)
              this.itemRemoved(tag, this.keyContext)
            }
          }
        },
        onFocus: this.focus || null,
        onBlur: this.blur || null,
        load: this.fetchResults || null,
        loadMore: this.fetchMoreResults || null,
        preload: this.preLoad,
        render: this.render,
        onChange: () => {
          this.selectize.$dropdown_content.removeHighlight()
        },
        onType: (string) => {
          if (!string) {
            this.selectize.$dropdown_content.removeHighlight()
          }
        },
        onDelete: () => {
          setTimeout(() => {
            this.selectize.$dropdown_content.removeHighlight()
          }, 0)
        },
        score: search => {
          let score = this.selectize.getScoreFunction(search)

          return function (item) {
            if (item.id === null || item.id === undefined || item.id === '') {
              return 0
            }

            return score(item)
          }
        }
      })[0].selectize

      this.selectize.setValue(map(this.value, (tag) => { return tag.id }), true)

      this.toggleDisabled(this.disabled)
    },

    /**
     * Toggle disabled.
     *
     * @param boolean disabled
     *
     * @return void
     */
    toggleDisabled (disabled) {
      if (disabled) {
        this.selectize.disable()
      } else {
        this.selectize.enable()
      }

      this.selectize.setValue(this.value, true)
    },

    /**
     * Add a new selected tag.
     *
     * @param object tag
     */
    addSelectedTag (tag) {
      if (!this.tagAlreadySelected(tag)) {
        this.selectedTags.push(tag)
      }
    },

    /**
     * Determine if a tag is already selected. Tag can be an integer or object.
     *
     * @param int|object tag
     *
     * @return bool
     */
    tagAlreadySelected (tag) {
      let id = tag.id || parseInt(tag)

      return find(this.selectedTags, (existing) => existing.id === id) !== undefined
    },

    /**
     * Clears the selected input.
     *
     * @return void
     */
    clear () {
      this.selectize.clear()

      this.$emit('input', null)
    },

    /**
     * Handle the changed event, emits the input value up to the parent component.
     *
     * @return void
     */
    handleChanged (value) {
      this.$emit('input', value)
    },

    /**
     * Converts value to a number if it should be a number.
     *
     * For strict type comparisons.
     *
     * @param mixed value
     *
     * @return mixed
     */
    shouldBeNumber (value) {
      if (value === null || value === '') {
        return null
      } else if (typeof value === 'object') {
        return value
      }

      let number = toNumber(value)

      if (isNaN(number)) {
        return value
      }

      return number
    },

    /**
     * Determine if value is out of bounds of options.
     *
     * @param mixed value
     * @param array options
     *
     * @return boolean
     */
    isValueOutOfBounds (value, options) {
      return filter(options, option => option.id === value).length === 0
    },

    /**
     * Remove a tag from the selected tags.
     *
     * @param object tag
     *
     * @return void
     */
    removeTag (tag) {
      this.selectedTags = filter(this.selectedTags, existing => String(existing.id) !== String(tag.id))

      this.selectize.removeItem(tag.id, true)
    }
  }
}
</script>
